<template>
    <div v-if="showTaskSummary" class="box" data-testid="tasks-summary-banner">
        <g-base-box>
            <g-media-object vertical-align="center">
                <template #left>
                    <g-typography tag="p" font-weight="semibold" value="Tasks Summary" />
                    <g-typography
                        v-if="numberOfRemainingTasks"
                        tag="p"
                        color="grey-6"
                        :value="`${numberOfRemainingTasks} remaining`"
                    />
                    <g-typography v-else tag="p" color="grey-6">
                        All tasks complete! <g-icon name="sparkles" color="grey-5" type="solid" />
                    </g-typography>
                </template>
                <template #right>
                    <g-button shape="round" theme="live" @click="handleGoToTaskSummary">{{ buttonCopy }}</g-button>
                </template>
            </g-media-object>
        </g-base-box>
    </div>
</template>

<script>
    import { computed } from 'vue-demi';
    import moment from 'moment';
    import {
        ACTIVATION_BRIEF_STATUS_ACCEPTED,
        ACTIVATION_BRIEF_TYPE_SCHEDULED
    } from '../../../../constants/activation-brief';
    import { ACTIVATION_TASK_STATUS_EVERGREEN } from '../../../../constants/activation-task';
    import { EVENT_NAME_DASHBOARD_TASKS_SUMMARY_BANNER_CLICKED } from '../../../../constants/analytics-events';
    import { useContactStore, useFeatureFlagsStore } from '../../../../../stores';
    import useEventTracking from '../../../../../src/composables/use-event-tracking';

    export default {
        name: 'TasksSummaryBanner',

        setup(_, { root }) {
            const flags = useFeatureFlagsStore().data;
            const contact = useContactStore().data;

            const numberOfRemainingTasks = computed(() => {
                let tasksCount = 0;
                root.briefs?.forEach((brief) => {
                    if (!briefIsValid(brief)) {
                        return;
                    }

                    const tasks = brief.tasks?.filter((task) => {
                        return taskIsNotComplete(task) && task.is_selected;
                    });

                    tasksCount += tasks?.length || 0;
                });

                return tasksCount;
            });

            const buttonCopy = computed(() => {
                return numberOfRemainingTasks.value ? 'Deliver content' : 'Review content';
            });

            const taskIsNotComplete = (task) => {
                return (
                    task.status === ACTIVATION_TASK_STATUS_EVERGREEN ||
                    (task.status !== ACTIVATION_TASK_STATUS_EVERGREEN && task.quantity > task.complete_quantity)
                );
            };

            const contentTrackingHasStarted = (brief) => {
                return brief.content_tracking_start_at ? moment(brief.content_tracking_start_at) < moment() : true;
            };

            const briefAccepted = (brief) => {
                return brief.status === ACTIVATION_BRIEF_STATUS_ACCEPTED;
            };

            const briefIsScheduled = (brief) => {
                return brief.type === ACTIVATION_BRIEF_TYPE_SCHEDULED;
            };

            const briefIsValid = (brief) => {
                return (
                    briefAccepted(brief) &&
                    (!briefIsScheduled(brief) || (briefIsScheduled(brief) && contentTrackingHasStarted(brief)))
                );
            };

            const hasValidBrief = computed(() => {
                return root.briefs.filter((brief) => briefIsValid(brief))?.length || false;
            });

            const showTaskSummary = computed(() => {
                return flags['task-delivery-improvements-enabled'] && hasValidBrief.value;
            });

            const handleGoToTaskSummary = () => {
                useEventTracking().trackEvent(EVENT_NAME_DASHBOARD_TASKS_SUMMARY_BANNER_CLICKED, {
                    contact_id: contact?.id
                });

                root.$router?.push({ name: 'v2.activation.tasks.summary' });
            };

            return {
                showTaskSummary,
                numberOfRemainingTasks,
                buttonCopy,
                handleGoToTaskSummary,
                briefAccepted,
                briefIsScheduled,
                briefIsValid,
                contentTrackingHasStarted,
                taskIsNotComplete
            };
        }
    };
</script>
